/**
 * hg-dynamic-price-badge.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Keeps the native HealthyGum price block (.shop-product-price-block,
 * .shop-compare-price, .shop-save-price) in sync with the Kaching bundle
 * selector whenever the user:
 *   • clicks a different bundle bar (quantity tier)
 *   • toggles the subscription checkbox
 *
 * Strategy
 * ────────
 * 1. Wait for the Kaching custom element to finish rendering
 *    (same `.kaching-bundles__block--loaded` guard used by the existing
 *    kaching-sync script).
 * 2. After each `variants-changed` event (or bar click / checkbox toggle),
 *    read the selected bar's price text directly from the Kaching DOM:
 *      .kaching-bundles__bar--selected .kaching-bundles__bar-price
 *      .kaching-bundles__bar--selected .kaching-bundles__bar-full-price
 * 3. Parse the numeric values, compute % saved, then write to:
 *      .shop-product-price-block   → current price
 *      .shop-compare-price         → compare-at price (strikethrough)
 *      .shop-save-price            → "SPRING SALE - SAVE X%" badge
 *    The save-badge prefix text is read from data-save-prefix on the span
 *    (injected by the Liquid template) so it stays localisation-safe.
 *
 * No external dependencies. Safe to load async / defer.
 */

(function () {
  'use strict';

  /* ── Selectors ─────────────────────────────────────────────────────────── */
  const SEL_KACHING        = 'kaching-bundles-block';
  const SEL_LOADED         = '.kaching-bundles__block--loaded';
  const SEL_SELECTED_PRICE = '.kaching-bundles__bar--selected .kaching-bundles__bar-price';
  const SEL_SELECTED_COMP  = '.kaching-bundles__bar--selected .kaching-bundles__bar-full-price';
  const SEL_PRICE_BLOCK    = '.shop-product-price-block';
  const SEL_COMPARE        = '.shop-compare-price';
  const SEL_SAVE_BADGE     = '.shop-save-price';

  /* ── Helpers ───────────────────────────────────────────────────────────── */

  /**
   * Extract a numeric price value from a formatted price string.
   * Handles: "NZD $33.95", "£33.95", "USD 33.95", "Dhs. 112.36", "$33.95"
   * Returns the float value in the display currency unit (not cents).
   */
  function parseDisplayPrice(text) {
    if (!text) return null;
    // Strip currency codes and symbols, keep digits, comma, dot, minus
    const cleaned = text
      .replace(/[A-Z]{3}\s*/g, '')   // strip 3-letter currency codes
      .replace(/[^0-9.,\-]/g, ' ')   // replace non-numeric chars with space
      .trim();
    // Take the last number-like token (handles "Dhs. 112.36" → "112.36")
    const tokens = cleaned.split(/\s+/).filter(Boolean);
    for (let i = tokens.length - 1; i >= 0; i--) {
      const n = parseFloat(tokens[i].replace(',', '.'));
      if (!isNaN(n) && n > 0) return n;
    }
    return null;
  }

  /**
   * Get the text content of the first text node inside an element,
   * ignoring child elements (e.g. the SVG icon inside .shop-save-price).
   */
  function getDirectTextContent(el) {
    for (const node of el.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        const t = node.textContent.trim();
        if (t) return t;
      }
    }
    return el.textContent.trim();
  }

  /**
   * Set only the direct text node(s) of an element, leaving child elements
   * (e.g. the SVG tag icon) intact.
   */
  function setDirectText(el, text) {
    // Remove existing text nodes
    for (const node of [...el.childNodes]) {
      if (node.nodeType === Node.TEXT_NODE) {
        el.removeChild(node);
      }
    }
    // Append new text node at the end
    el.appendChild(document.createTextNode(' ' + text));
  }

  /* ── Core logic ────────────────────────────────────────────────────────── */

  function initBadge(kEl) {
    const priceBlock = document.querySelector(SEL_PRICE_BLOCK);
    const compareEl  = document.querySelector(SEL_COMPARE);
    const badgeEl    = document.querySelector(SEL_SAVE_BADGE);

    if (!priceBlock && !compareEl && !badgeEl) {
      console.warn('[hg-badge] No price elements found — skipping init.');
      return;
    }

    /**
     * Read the save-badge prefix text.
     * Priority:
     *   1. data-save-prefix attribute on .shop-save-price (if added by Liquid)
     *   2. The existing text before the last word (percentage), e.g. "SPRING SALE - SAVE"
     *   3. Fallback: "SAVE"
     */
    function getSavePrefix() {
      if (badgeEl) {
        if (badgeEl.dataset.savePrefix) return badgeEl.dataset.savePrefix;
        // Read the current badge text and strip the trailing "XX%"
        const raw = badgeEl.textContent.trim();
        const match = raw.match(/^(.*?)\s*\d+%\s*$/);
        if (match && match[1].trim()) return match[1].trim();
      }
      return 'SPRING SALE - SAVE';
    }

    // Cache the prefix once on first call (it never changes)
    let _savePrefix = null;
    function savePrefix() {
      if (!_savePrefix) _savePrefix = getSavePrefix();
      return _savePrefix;
    }

    function syncPrices() {
      const priceTextEl = kEl.querySelector(SEL_SELECTED_PRICE);
      const compTextEl  = kEl.querySelector(SEL_SELECTED_COMP);

      if (!priceTextEl) {
        console.warn('[hg-badge] Selected bar price element not found.');
        return;
      }

      const currentText  = priceTextEl.textContent.trim();
      const compareText  = compTextEl ? compTextEl.textContent.trim() : null;

      const currentVal   = parseDisplayPrice(currentText);
      const compareVal   = compareText ? parseDisplayPrice(compareText) : null;

      console.log('[hg-badge] syncPrices — current:', currentText, '→', currentVal,
                  '| compare:', compareText, '→', compareVal);

      // Update current price
      if (priceBlock && currentText) {
        priceBlock.textContent = currentText;
      }

      // Update compare-at price and save badge
      if (compareVal && currentVal && compareVal > currentVal) {
        const pctSaved = Math.round((compareVal - currentVal) / compareVal * 100);

        if (compareEl && compareText) {
          compareEl.textContent = compareText;
          compareEl.style.display = '';
        }

        if (badgeEl) {
          setDirectText(badgeEl, savePrefix() + ' ' + pctSaved + '%');
          badgeEl.style.display = '';
        }

        // Ensure on-sale class is present
        if (priceBlock) priceBlock.classList.add('on-sale');

      } else {
        // No discount — hide compare price and badge
        if (compareEl)  compareEl.style.display  = 'none';
        if (badgeEl)    badgeEl.style.display     = 'none';
        if (priceBlock) priceBlock.classList.remove('on-sale');
      }
    }

    // ── Listen for Kaching events ─────────────────────────────────────────
    kEl.addEventListener('variants-changed', () => {
      // Small delay to let Kaching finish updating its own DOM
      setTimeout(syncPrices, 80);
    });

    // Also watch for bar clicks directly (belt-and-suspenders)
    kEl.addEventListener('click', (e) => {
      const bar = e.target.closest('[data-deal-bar-id]');
      if (bar) setTimeout(syncPrices, 80);
    });

    // Watch subscription checkbox toggle
    const cbEl = kEl.querySelector('.kaching-bundles__subscriptions [role="checkbox"]');
    if (cbEl) {
      new MutationObserver(() => {
        setTimeout(syncPrices, 80);
      }).observe(cbEl, { attributes: true, attributeFilter: ['aria-checked'] });
    }

    // Initial sync
    syncPrices();
    // Second sync after Kaching's own delayed init
    setTimeout(syncPrices, 350);

    console.log('[hg-badge] Initialised ✓');
  }

  /* ── Bootstrap: wait for Kaching to render ─────────────────────────────── */

  function observeBlock(kEl) {
    if (kEl.querySelector(SEL_LOADED)) {
      initBadge(kEl);
      return;
    }
    const mo = new MutationObserver((_, obs) => {
      if (kEl.querySelector(SEL_LOADED)) {
        obs.disconnect();
        initBadge(kEl);
      }
    });
    mo.observe(kEl, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  function boot() {
    const kEl = document.querySelector(SEL_KACHING);
    if (kEl) {
      observeBlock(kEl);
    } else {
      // Kaching element not yet in DOM — wait for it
      const docObs = new MutationObserver((_, obs) => {
        const el = document.querySelector(SEL_KACHING);
        if (el) { obs.disconnect(); observeBlock(el); }
      });
      docObs.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
